import { z } from "zod";
import type {
  AdditionalFieldItem,
  AdditionalFieldItemInput,
} from "./additionalFields";
import {
  PAST_DAYS_MIN,
  PAST_DAYS_MAX,
  PAST_DAYS_MIN_MESSAGE,
  PAST_DAYS_MAX_MESSAGE,
  PAST_DAYS_TYPE_MESSAGE,
} from "@/utils/pastDaysValidation";

/**
 * 追加項目のフィールドタイプの定数定義
 * UserConfigFormDataSchema と NewSavedMapPlotRequestDetailAdditionalFieldSchema で共有
 */
export const ADDITIONAL_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "date",
  "datetime",
  "boolean",
] as const;

/**
 * ユーザー設定取得APIのレスポンス型
 */
export interface UserConfigAPIResponse {
  pictureRequestPastDays: number;
  isPriceHidden: boolean;
  mapChibanIsMatch: boolean;
  mapZenrinChibanIncludesTbn: boolean;
  mapPlotRequestDetailAdditionalFields?: {
    fields: AdditionalFieldItem[];
  };
}

/**
 * ユーザー設定更新APIのレスポンス型
 */
export interface UserConfigUpdateAPIResponse
  extends Omit<UserConfigAPIResponse, "isPriceHidden"> {
  mapInitLat: number;
  mapInitLon: number;
}

export const UserConfigFormDataSchema = z.object({
  pictureRequestPastDays: z
    .number({ invalid_type_error: PAST_DAYS_TYPE_MESSAGE })
    .min(PAST_DAYS_MIN, PAST_DAYS_MIN_MESSAGE)
    .max(PAST_DAYS_MAX, PAST_DAYS_MAX_MESSAGE),
  mapInitLat: z
    .number({ invalid_type_error: "緯度を入力してください" })
    .min(-90, "緯度は-90〜90の範囲で入力してください")
    .max(90, "緯度は-90〜90の範囲で入力してください"),
  mapInitLon: z
    .number({ invalid_type_error: "経度を入力してください" })
    .min(-180, "経度は-180〜180の範囲で入力してください")
    .max(180, "経度は-180〜180の範囲で入力してください"),
  mapChibanIsMatch: z.boolean(),
  mapZenrinChibanIncludesTbn: z.boolean(),
  mapPlotRequestDetailAdditionalFields: z
    .object({
      fields: z.array(
        z.object({
          id: z.string(),
          key: z.string(),
          fieldType: z.enum(ADDITIONAL_FIELD_TYPES),
          createdAt: z.string(),
          deletedAt: z.string().nullable().optional(),
        })
      ),
    })
    .optional(),
});

// 定義したZodのスキーマをTypescriptの型に変換
export type UserConfigFormData = z.infer<typeof UserConfigFormDataSchema>;

/**
 * ユーザー設定更新時のリクエストデータ型（送信用）
 * 追加項目フィールドは NewAdditionalFieldItem | AdditionalFieldItem の配列
 */
export interface UserConfigUpdateRequest
  extends Omit<UserConfigFormData, "mapPlotRequestDetailAdditionalFields"> {
  mapPlotRequestDetailAdditionalFields?: {
    fields: AdditionalFieldItemInput[];
  };
}

export const ChangePasswordFormSchema = z
  .object({
    currentPassword: z
      .string()
      .min(8, "パスワードは8文字以上で入力してください"),
    newPassword: z
      .string()
      .min(8, "パスワードは8文字以上で入力してください")
      .superRefine((val, ctx) => {
        if (/^\d+$/.test(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "パスワードには数字以外も含めてください",
          });
        }
      }),
    newPasswordConfirmation: z.string(),
  })
  .superRefine((data, ctx) => {
    if (data.newPassword !== data.newPasswordConfirmation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "パスワードが一致しません",
        path: ["newPasswordConfirmation"],
      });
    }
  });

export type ChangePasswordFormData = z.infer<typeof ChangePasswordFormSchema>;

export const CompanyInfoFormDataSchema = z.object({
  companyOverview: z.string().max(500, "500文字以下で入力してください"),
});
export type CompanyInfoFormData = z.infer<typeof CompanyInfoFormDataSchema>;

export const CompanyLogoFormDataSchema = z.object({
  file: z.instanceof(File).nullable(),
  url: z.string().url().nullable(),
});
export type CompanyLogoFormData = z.infer<typeof CompanyLogoFormDataSchema>;

// 新規項目追加フォームのスキーマ
export const NewSavedMapPlotRequestDetailAdditionalFieldSchema = z.object({
  label: z
    .string()
    .min(1, "項目名を入力してください")
    .max(20, "項目名は20文字以内で入力してください"),
  fieldType: z.enum(ADDITIONAL_FIELD_TYPES),
});

export type NewSavedMapPlotRequestDetailAdditionalFieldFormData = z.infer<
  typeof NewSavedMapPlotRequestDetailAdditionalFieldSchema
>;
